import { createSlice } from "@reduxjs/toolkit";
import type { CategorySliceTypes } from "../../types/category/category.model";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";
import type {
  CreateCategoryPayload,
  GetAllCategoryPayload,
  GetCategoriesByParentPayload,
  GetCategoriesPayload,
  updateCategoryPayload,
} from "../../types/category/category.payload";
import type {
  CreateCategoryResponse,
  GetAllCategoryResponse,
  GetCategoriesByParentResponse,
  GetCategoriesResponse,
  GetCategoryResponse,
  updateCategoryResponse,
} from "../../types/category/category.response";

const initialState: CategorySliceTypes = {
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    all: false,
    byParent: false,
  },
  error: null,
  category: null,
  categories: [],
  pagination: null,
  allCategory: [],
  byParent: {},
};

export const getCategories = createApiThunk<
  GetCategoriesResponse,
  GetCategoriesPayload
>(
  "category/getCategories",
  async (query) =>
    (await axiosClient.get(API.category.list, { params: query })).data,
  "Lấy danh sách danh mục thất bại",
);

export const getAllCategory = createApiThunk<
  GetAllCategoryResponse,
  GetAllCategoryPayload
>(
  "category/getAllCategory",
  async (query) =>
    (await axiosClient.get(API.category.all, { params: query })).data,
  "Lấy tất cả danh mục thất bại",
);

export const createCategory = createApiThunk<
  CreateCategoryResponse,
  CreateCategoryPayload
>(
  "category/createCategory",
  async (data) => (await axiosClient.post(API.category.create, data)).data,
  "Tạo danh mục thất bại",
);

export const getDetail = createApiThunk<GetCategoryResponse, string>(
  "category/getDetail",
  async (id) => (await axiosClient.get(API.category.detail(id))).data,
  "Lấy thông tin danh mục thất bại",
);

export const updateCategory = createApiThunk<
  updateCategoryResponse,
  updateCategoryPayload
>(
  "category/updateCategory",
  async ({ id, data }) =>
    (await axiosClient.put(API.category.update(id), data)).data,
  "Cập nhật danh mục thất bại",
);

export const deleteCategory = createApiThunk<string, string>(
  "category/deleteCategory",
  async (id) => (await axiosClient.delete(API.category.delete(id))).data,
  "Xóa danh mục thất bại",
);

export const getCategoriesByParent = createApiThunk<
  GetCategoriesByParentResponse,
  GetCategoriesByParentPayload
>(
  "category/getCategoriesByParent",
  async (query) =>
    (await axiosClient.get(API.category.selectCategory, { params: query }))
      .data,
  "Lấy danh mục thất bại",
);

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: getCategories,
      loadingKey: "list",
      fallbackError: "Lấy danh sách danh mục thất bại",
      onSuccess: (state, payload) => {
        state.categories = payload.categories;
        state.pagination = payload.pagination;
      },
    });
    addAsyncCases({
      builder,
      thunk: getAllCategory,
      loadingKey: "all",
      fallbackError: "Lấy tất cả danh mục thất bại",
      onSuccess: (state, payload) => {
        state.allCategory = payload.allCategory;
      },
    });
    addAsyncCases({
      builder,
      thunk: createCategory,
      loadingKey: "create",
      fallbackError: "Tạo danh mục thất bại",
      onSuccess: (state, payload) => {
        state.category = payload.category;
      },
    });
    addAsyncCases({
      builder,
      thunk: getDetail,
      loadingKey: "detail",
      fallbackError: "Lấy thông tin danh mục thất bại",
      onSuccess: (state, payload) => {
        state.category = payload.category;
      },
    });
    addAsyncCases({
      builder,
      thunk: updateCategory,
      loadingKey: "update",
      fallbackError: "Cập nhật danh mục thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.updatedCategory;
        const idx = state.categories.findIndex((c) => c._id === updated._id);
        if (idx !== -1) state.categories[idx] = updated;
      },
    });
    addAsyncCases({
      builder,
      thunk: deleteCategory,
      loadingKey: "delete",
      fallbackError: "Xóa danh mục thất bại",
      onSuccess: (state, payload) => {
        state.categories = state.categories.filter((c) => c._id !== payload);
      },
    });
    addAsyncCases({
      builder,
      thunk: getCategoriesByParent,
      loadingKey: "byParent",
      fallbackError: "Lấy danh mục thất bại",
      onSuccess: (state, payload) => {
        const { parentId, categories } = payload;
        const key = parentId ?? "null";
        state.byParent[key] = categories;
      },
    });
  },
});

export default categorySlice.reducer;
